import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import * as moment from 'moment';
import { debounceTime, fromEvent } from 'rxjs';
import SignaturePad from 'signature_pad';

/**
 * Shows the invalid (red outline) state as soon as the field is rendered, instead of
 * waiting for the user to touch or dirty the control. The signatory fields are known to
 * be missing when the step opens, so surface that up front.
 */
export class ImmediateErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: AbstractControl | null): boolean {
    return !!control && control.invalid;
  }
}

@Component({
  selector: 'app-signature-pad',
  templateUrl: './signature-pad.component.html',
  styleUrls: ['./signature-pad.component.scss']
})
export class SignaturePadComponent implements OnInit, AfterViewInit {
  private signaturePad!: SignaturePad;

  @ViewChild('canvas', { read: ElementRef }) canvasRef?: ElementRef;

  @Input() points: any[] = [];
  @Output() pointsChange = new EventEmitter<any>();

  @Input() editable = true;
  @Input() emitOnDragEnd = false;

  @Input() penColor = 'black';
  @Input() backgroundColor = 'white';

  @Input() showDoneButton = false;
  @Input() doneButtonText = 'Done';
  @Input() doneButtonClass = 'btn btn-primary';
  @Input() showClearButton = false;
  @Input() clearButtonText = 'Clear';
  @Input() clearButtonClass = 'btn btn-default';

  @Input() format: 'blob' | 'base64' | 'json' | 'jpeg' = 'blob';

  @Input() height = 150;
  @Input() width = 600;

  @Output() done = new EventEmitter();
  @Output() cleared = new EventEmitter();
  @Output() validationChanged = new EventEmitter<boolean>();

  @Input() responsive = true;

  @Input() hideDetails = false;
  @Input() signatoryRequired = false;

  signAsGuardian = false;
  guardianName = '';
  signatoryDelegation = '';

  readonly signatoryErrorMatcher = new ImmediateErrorStateMatcher();

  today?: string;

  constructor(private renderer: Renderer2, private zone: NgZone) {
    this.adjustCanvasSize();
  }

  ngOnInit() {
    this.today = moment().format('DD/MM/YYYY');
  }

  ngAfterViewInit() {
    this.zone.runOutsideAngular(() => {
      if (this.responsive) {
        window.addEventListener('resize', () => {
          this.resizeCanvas();
        });
        this.resizeCanvas();
      }
      this.initPad();
    });
  }

  initPad() {
    const canvas = this.canvasRef!.nativeElement;

    this.signaturePad = new SignaturePad(canvas, {
      penColor: this.penColor,
      backgroundColor: this.backgroundColor,
      minWidth: 0.2,
      maxWidth: 1.0,
      compositeOperation: "source-over",
      velocityFilterWeight: 0.2,
      minDistance: 2,
      canvasContextOptions: { desynchronized: true },
    });

    if (this.editable) {
      this.signaturePad.on();
    } else {
      this.signaturePad.off();
    }

    // Event handlers for signature end actions
    this.bindEndEvents(canvas);

    this.applyPoints();
  }

  bindEndEvents(canvas: HTMLCanvasElement) {
    // Primary 'end' events for finishing the stroke
    const endEvents = ['pointerup', 'mouseup', 'touchend'];

    // Optional: Additional edge/cancel events
    const cancelEvents = ['pointercancel', 'touchcancel'];

    // Optional: If we want to treat leaving the canvas as an 'end'
    // (use carefully, in this case we want to handle partial signatures, etc.)
    const leaveEvents = ['pointerleave', 'mouseleave'];

    [...endEvents, ...cancelEvents, ...leaveEvents].forEach(eventType => {
      fromEvent(canvas, eventType)
        .pipe(
          debounceTime(10) // Avoid multiple triggers in quick succession
        )
        .subscribe(() => {
          this.emitPoints();
          if (this.emitOnDragEnd) {
            this.emitBlob();
          }
        });
    });
  }

  clearPad() {
    this.signaturePad.clear();
    this.initPad();
    this.cleared.emit();
    this.emitValidationChange();
    /*  this.emitPoints();
     if (this.emitOnDragEnd) {
       this.emitBlob();
     } */
  }

  applyPoints() {
    if (!this.points || !this.signaturePad) { return; }

    this.signaturePad.clear();

    const multiplier = this.canvasRef!.nativeElement.offsetWidth / this.width;

    const points = JSON.parse(JSON.stringify(this.points));
    points.forEach((group: any) => {
      group.points.forEach((pt: any) => {
        pt.x = pt.x * multiplier;
        pt.y = pt.y * multiplier;
      });
    });
    this.signaturePad.fromData(points);
  }

  emitPoints() {
    const multiplier = this.canvasRef!.nativeElement.offsetWidth / this.width;
    const points = JSON.parse(JSON.stringify(this.signaturePad.toData()));
    points.forEach((group: any) => {
      group.points.forEach((pt: any) => {
        pt.x = pt.x / multiplier;
        pt.y = pt.y / multiplier;
      });
    });
    this.pointsChange.emit(points);
    this.emitValidationChange();
  }

  emitBlob() {
    switch (this.format) {
      case 'jpeg':
        this.done.emit(this.signaturePad.toDataURL('image/jpeg'));
        break;

      case 'base64':
        this.done.emit(this.signaturePad.toDataURL());
        break;

      case 'json':
        this.done.emit(this.signaturePad.toData());
        break;

      default:
        this.canvasRef!.nativeElement.toBlob((blob: any) => {
          this.done.emit(blob);
        });
        break;
    }
  }

  getPadData() {
    switch (this.format) {
      case 'jpeg':
        return this.signaturePad.toDataURL('image/jpeg');
      case 'base64':
        return this.signaturePad.toDataURL();
      case 'json':
        return this.signaturePad.toData();
      default:
        return this.canvasRef!.nativeElement.toBlob((blob: any) => {
          return blob;
        });
    }
  }

  emitPointsAndBlob() {
    this.emitPoints();
    this.emitBlob();
  }

  resizeCanvas() {
    const canvas = this.canvasRef!.nativeElement;
    if (!canvas) { return; }

    const pad = canvas.closest('.signature-pad') as HTMLElement;
    if (!pad) { return; }

    const computedStyle = window.getComputedStyle(pad);
    const w = parseInt(computedStyle.width, 10);
    const h = w / (this.width / this.height);

    this.renderer.setAttribute(canvas, 'width', `${w}`);
    this.renderer.setAttribute(canvas, 'height', `${h}`);

    this.applyPoints();
  }

  adjustCanvasSize() {
    if (this.isMobile()) {
      this.width = 300;
      this.height = 100;
      this.responsive = false;
    }
  }

  isMobile() {
    if (typeof window === 'undefined') {
      return false; // Ensures safe execution on the server
    }
    return window.innerWidth <= 768;
  }

  // Method to check if any signature has been drawn
  isSignatureDrawn(): boolean {
    return !this.signaturePad.isEmpty();
  }

  // Method to check if the signature pad is valid
  isValid(): boolean {
    // Check if signature is drawn
    if (!this.isSignatureDrawn()) {
      return false;
    }

    // If signatory fields are required, validate them
    if (this.signatoryRequired) {
      if (!this.guardianName || !this.guardianName.trim()) {
        return false;
      }
      if (!this.signatoryDelegation || !this.signatoryDelegation.trim()) {
        return false;
      }
    }

    return true;
  }

  // Method to emit validation changes
  private emitValidationChange() {
    if (this.signatoryRequired) {
      this.validationChanged.emit(this.isValid());
    }
  }

  // Method to handle guardian name changes
  onGuardianNameChange() {
    this.emitValidationChange();
  }

  // Method to handle delegation changes
  onDelegationChange() {
    this.emitValidationChange();
  }

  // Method to get validation errors
  getValidationErrors(): string[] {
    const errors: string[] = [];
    
    if (this.signatoryRequired) {
      if (!this.guardianName || !this.guardianName.trim()) {
        errors.push('Signatory name is required');
      }
      if (!this.signatoryDelegation || !this.signatoryDelegation.trim()) {
        errors.push('Delegation is required');
      }
    }
    
    if (!this.isSignatureDrawn()) {
      errors.push('Signature is required');
    }
    
    return errors;
  }
}
